// src/features/dashboard/components/ReservationsCard.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  CalendarClock,
  ChevronDown,
  ChevronUp,
  PlusCircle,
  Link as LinkIcon,
  MapPin,
  User,
  Copy,
  Loader2,
  RotateCcw,
  MessageCircle,
} 
from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions, auth } from "../../../firebase";
import { TENANT_ID } from "../../../tenant";
import ShareLinkModal from "./ShareLinkModal";

// ✅ Date / Firestore Timestamp / number / string を安全に Date にする
const toDateSafe = (v) => {
  if (!v) return null;

  // Firestore Timestamp (has toDate)
  if (typeof v?.toDate === "function") {
    try {
      const d = v.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }

  // Date
  if (v instanceof Date) return !Number.isNaN(v.getTime()) ? v : null;

  // number (ms)
  if (typeof v === "number") {
    const d = new Date(v);
    return !Number.isNaN(d.getTime()) ? d : null;
  }

  // string (ISO etc.)
  if (typeof v === "string") {
    const d = new Date(v);
    return !Number.isNaN(d.getTime()) ? d : null;
  }

  return null;
};

const formatTimeRange = (start, end) => {
  const s = toDateSafe(start);
  const e = toDateSafe(end);

  if (!s || !e) return "";

  const format = (d) =>
    d.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

  return `${format(s)} 〜 ${format(e)}`;
};

const clampInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : def;
};

const pickGuestCount = (res) =>
  clampInt(res?.guest_count ?? res?.guestCount ?? res?.guests ?? res?.guest ?? 1, 1) || 1;

const pickIssuedCount = (res) =>
  clampInt(
    res?.invite_issued_count ??
      res?.invites_issued_count ??
      res?.inviteIssuedCount ??
      res?.invitesIssuedCount ??
      0,
    0
  );

const pickClaimedCount = (res) =>
  clampInt(
    res?.invite_claimed_count ??
      res?.invites_claimed_count ??
      res?.inviteClaimedCount ??
      res?.invitesClaimedCount ??
      0,
    0
  );

const getRealInviteUrls = (urls) =>
  Array.from(
    new Set(
      (Array.isArray(urls) ? urls : [])
        .filter((u) => !isPendingUrl(u))
        .map((u) => String(u || "").trim())
        .filter(Boolean)
    )
  );

const getIssuedCountForReservation = (res, uiUrls = []) => {
  const guestCount = pickGuestCount(res);
  const realUrls = getRealInviteUrls(uiUrls);
  const issuedFromDoc = pickIssuedCount(res);

  // Firestoreの issued_count と、画面上で保持している実URL数の大きい方を採用
  return Math.min(guestCount, Math.max(issuedFromDoc, realUrls.length));
};

const getRemainingCountForReservation = (res, uiUrls = []) => {
  const guestCount = pickGuestCount(res);
  const issued = getIssuedCountForReservation(res, uiUrls);
  return Math.max(0, guestCount - issued);
};




// ✅ 場所表示（tenant化でフィールド差が出ても吸収）
const getLocationLabel = (res) => {
  const candidates = [
    res?.locationName,
    res?.location_label,
    res?.location,
    res?.place,
    res?.storeName,
    res?.store_name,
    res?.roomName,
    res?.room_name,
    res?.metadata?.locationName,
    res?.metadata?.location,
  ]
    .filter((v) => typeof v === "string")
    .map((v) => v.trim())
    .filter(Boolean);

  return candidates[0] || "—";
};

const getReservationLocationName = (res) => {
  return (
    res?.locationSnapshot?.name ||
    getLocationLabel(res) ||
    res?.tenantId ||
    TENANT_ID ||
    "—"
  );
};

const getReservationAddress = (res) => {
  return (
    res?.locationSnapshot?.address ||
    ""
  );
};

const getReservationMapUrl = (res) => {
  return (
    res?.locationSnapshot?.googleMapsUrl ||
    ""
  );
};

// =========================
// ✅ 招待リンクのローカル永続化（urls + copied）
// =========================
const INVITE_STORAGE_PREFIX = "suomi_invite_state_v1:"; // key: prefix + tenantId + ":" + reservationId
const FALLBACK_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const END_GRACE_MS = 24 * 60 * 60 * 1000; // endAt + 24h

const buildStorageKey = (tenantId, reservationId) =>
  `${INVITE_STORAGE_PREFIX}${String(tenantId || "")}:${String(reservationId || "")}`;

const safeJsonParse = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

const normalizeUrls = (urls) =>
  Array.from(new Set((Array.isArray(urls) ? urls : []).map((u) => String(u || "").trim()).filter(Boolean)));

const normalizeCopied = (copiedAny, urls) => {
  const urlList = normalizeUrls(urls);
  const set = new Set(urlList);

  const copied = copiedAny && typeof copiedAny === "object" ? copiedAny : {};
  const out = {};
  Object.keys(copied).forEach((k) => {
    const key = String(k || "").trim();
    if (!key) return;
    if (!set.has(key)) return; // もう存在しないURLは落とす
    out[key] = copied[key] === true;
  });
  return out;
};

// ✅ 招待URL から token を抜く（/invite/reservation/:token を想定）
const extractInviteTokenFromUrl = (url) => {
  const s = String(url || "").trim();
  if (!s) return "";

  // 末尾が /invite/reservation/<token> の想定
  const m = s.match(/\/invite\/reservation\/([^/?#]+)/);
  if (m && m[1]) return String(m[1]).trim();

  // クエリに token= がある運用も一応救済
  try {
    const u = new URL(s);
    const t = u.searchParams.get("token");
    return t ? String(t).trim() : "";
  } catch {
    return "";
  }
};

// ✅ claimed tokens を reservation から作る（invite_claimed_users を正）
const buildClaimedTokenSet = (res) => {
  const arr = Array.isArray(res?.invite_claimed_users) ? res.invite_claimed_users : [];
  const tokens = arr
    .map((x) => String(x?.token || x?.token_id || "").trim())
    .filter(Boolean);
  return new Set(tokens);
};

const buildClaimedUserMap = (res) => {
  const arr = Array.isArray(res?.invite_claimed_users) ? res.invite_claimed_users : [];

  const map = new Map();

  arr.forEach((x) => {
    const token = String(x?.token || x?.token_id || "").trim();
    if (!token) return;

    const name = String(
      x?.name ||
      x?.customer_name ||
      x?.claimed_by_name ||
      ""
    ).trim();

    map.set(token, name || "参加者");
  });

  return map;
};

const computeExpiresAtMs = (res) => {
  const end = toDateSafe(res?.endAt);
  if (end) return end.getTime() + END_GRACE_MS;

  const start = toDateSafe(res?.startAt);
  if (start) return start.getTime() + 2 * 60 * 60 * 1000 + END_GRACE_MS; // start + 2h + grace

  return Date.now() + FALLBACK_TTL_MS;
};

const loadPersistedInviteState = (tenantId, reservationId) => {
  const key = buildStorageKey(tenantId, reservationId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { urls: [], copied: {} };

    const obj = safeJsonParse(raw);
    if (!obj || typeof obj !== "object") return { urls: [], copied: {} };

    const expiresAtMs = Number(obj.expiresAtMs || 0);
    if (expiresAtMs && Date.now() > expiresAtMs) {
      localStorage.removeItem(key);
      return { urls: [], copied: {} };
    }

    const urls = normalizeUrls(obj.urls);
    const copied = normalizeCopied(obj.copied, urls);
    return { urls, copied };
  } catch {
    return { urls: [], copied: {} };
  }
};

const savePersistedInviteState = (tenantId, reservationId, resForExpiry, urls, copied) => {
  const key = buildStorageKey(tenantId, reservationId);
  try {
    const urlsNorm = normalizeUrls(urls);
    const copiedNorm = normalizeCopied(copied, urlsNorm);

    const payload = {
      urls: urlsNorm,
      copied: copiedNorm,
      savedAtMs: Date.now(),
      expiresAtMs: computeExpiresAtMs(resForExpiry),
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // storage full / disabled 等は握りつぶし（動作優先）
  }
};

// =========================
// ✅ 招待受け取り（参加者）判定：auth_uid vs currentUser.uid
// =========================
const pickOwnerUid = (res) => {
  const candidates = [
    res?.auth_uid, // ★いまのログで見えている owner
    res?.authUid,
    res?.owner_auth_uid,
    res?.ownerAuthUid,
    res?.created_by_uid,
    res?.createdByUid,
    res?.invited_by_uid,
    res?.invitedByUid,
    res?.metadata?.auth_uid,
  ]
    .filter((v) => typeof v === "string")
    .map((v) => v.trim())
    .filter(Boolean);

  return candidates[0] || "";
};

const isInviteGuestReservation = (res, myUid) => {
  const ownerUid = pickOwnerUid(res);
  if (!myUid) return false;
  if (!ownerUid) return false; // owner不明は主催者扱い
  return ownerUid !== myUid;
};

// ✅ “この予約のドアはプラン会員なら開けられる” 判定（フィールド差を吸収）
const isPlanMemberForThisReservationDoor = (res) => {
  const v =
    res?.is_member ??
    res?.isMember ??
    res?.member ??
    res?.is_plan_member ??
    res?.isPlanMember ??
    res?.metadata?.is_member ??
    false;
  return v === true;
};

// ✅ 支払い方法（free判定）: フィールド差を吸収
const isFreeReservation = (res) => {
  const v =
    res?.paymentMethod ??
    res?.payment_method ??
    res?.payment?.method ??
    res?.metadata?.paymentMethod ??
    res?.metadata?.payment_method ??
    null;

  return String(v || "").toLowerCase() === "free";
};

// =========================
// ✅ 無料予約キャンセル仕様
// =========================
const FREE_CANCEL_DEADLINE_MIN = 60; // 開始の60分前まで
const canCancelFreeReservation = (res) => {
  const s = toDateSafe(res?.startAt);
  if (!s) return true; // startAt不明は「表示してる＝未来/ongoing」なので、守りとして許可（Functions側でもガード推奨）
  const deadline = s.getTime() - FREE_CANCEL_DEADLINE_MIN * 60 * 1000;
  return Date.now() <= deadline;
};

// =========================
// ✅ 招待リンク “プリロード” 用：pending 行判定＆生成
// =========================
const PENDING_URL_PREFIX = "__pending__invite__:"; // ここから始まるURLはUI上pending扱い
const isPendingUrl = (u) => String(u || "").startsWith(PENDING_URL_PREFIX);
const buildPendingUrls = (reservationId, count) => {
  const n = Math.max(1, Number(count || 1));
  const rid = String(reservationId || "").trim() || "unknown";
  return Array.from({ length: n }).map((_, i) => `${PENDING_URL_PREFIX}${rid}:${i}:${Date.now()}`);
};

const ReservationsCard = ({
  reservations = [],
  pendingReservationCount = 0,
  isOpen,
  onToggle,
  onOpenBooking,
  highlightReservationId = null,
  highlightInviteToken = null,
}) => {

const [shareModal, setShareModal] = useState({
  open: false,
  link: "",
  label: "",
  reservationId: "",
  dateTime: "",
  location: "",
  address: "",
  mapUrl: "",
});

    const [pendingAlertOpen, setPendingAlertOpen] = useState(false);

  // ✅ 「開いたままでも時間経過で消える」ための tick（30秒）
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  // =========================
  // ✅ 終了した予約は表示から除外（維持）
  // =========================
  const visibleReservations = useMemo(() => {
    const list = Array.isArray(reservations) ? reservations : [];
    const now = Date.now();

    // endAt が無い予約は「startAt + 2h」を仮 end として扱う（必要なら変更）
    const FALLBACK_DURATION_MIN = 120;

    const isFutureOrOngoing = (res) => {
      const s = toDateSafe(res?.startAt);
      const e = toDateSafe(res?.endAt);

      if (!s && !e) return true;
      if (e) return e.getTime() > now;

      if (s) {
        const endFallback = s.getTime() + FALLBACK_DURATION_MIN * 60 * 1000;
        return endFallback > now;
      }
      return true;
    };

    const sortByTime = (a, b) => {
      const as = toDateSafe(a?.startAt)?.getTime() ?? 0;
      const bs = toDateSafe(b?.startAt)?.getTime() ?? 0;
      return as - bs;
    };

    return list.filter(isFutureOrOngoing).sort(sortByTime);
  }, [reservations, nowTick]);

  const count = visibleReservations.length;
  const hasItems = count > 0;

  const topItem = hasItems ? visibleReservations[0] : null;
  const remainingCount = Math.max(0, count - 1);

  // 表示用：時間レンジ
  const formatDateTimeLabel = (start, end) => {
    const s = toDateSafe(start);
    const e = toDateSafe(end);

    if (!s) return "";

    const dateLabel = s.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      weekday: "short",
    });

    const startLabel = s.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    if (!e) return `${dateLabel} ${startLabel}`;

    const endLabel = e.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${dateLabel} ${startLabel}〜${endLabel}`;
  };

  const oneLineSummary = useMemo(() => {
    if (!hasItems) {
      return {
        icon: <CalendarClock size={18} className="text-gray-500" />,
        text: "",
      };
    }

    const start = toDateSafe(topItem?.startAt);
    const dateLabel = start
      ? `${start.toLocaleDateString("ja-JP", {
          month: "numeric",
          day: "numeric",
        })}(${start.toLocaleDateString("ja-JP", {
          weekday: "short",
        })})`
      : "";

    const title = String(topItem?.title || topItem?.serviceName || "予約あり");
    const trimmed = title.length > 16 ? `${title.slice(0, 16)}…` : title;

    const mainText = dateLabel ? `${dateLabel} ${trimmed}` : trimmed;
    const text = remainingCount > 0 ? `${mainText} 他${remainingCount}件` : mainText;

    return {
      icon: <CalendarClock size={18} className="text-gray-700" />,
      text,
    };
  }, [hasItems, topItem, remainingCount]);

  const summaryText = useMemo(() => {
    return isOpen ? "一覧を表示中" : oneLineSummary.text;
  }, [isOpen, oneLineSummary.text]);

  // =========================
  // ✅ 招待リンク UI state（予約ごと）
  // =========================
  // { [reservationId]: { loading, error, urls, copied: { [url]: true } } }
  const [inviteUi, setInviteUi] = useState({});

  const [pendingCancelLoading, setPendingCancelLoading] = useState(false);
  const [pendingCancelError, setPendingCancelError] = useState("");

  useEffect(() => {
    if (pendingReservationCount > 0) {
      setPendingAlertOpen(true);
    }
  }, [pendingReservationCount]);

  const onCancelAllPending = async () => {
    if (pendingCancelLoading) return;

    setPendingCancelLoading(true);
    setPendingCancelError("");

    try {
      const fn = httpsCallable(functions, "cancelAllPendingReservations");
      await fn({
        tenantId: TENANT_ID,
        reason: "user_cancelled_all_pending",
      });

      // ✅ reservations を onSnapshot で見てるなら、自動で pending が減って UI が消える
    } catch (e) {
      setPendingCancelError(e?.message || "キャンセルに失敗しました");
    } finally {
      setPendingCancelLoading(false);
    }
  };

  const setUiFor = useCallback((reservationId, patch) => {
    setInviteUi((prev) => {
      const cur = prev?.[reservationId] || { loading: false, error: "", urls: [], copied: {} };
      return { ...prev, [reservationId]: { ...cur, ...patch } };
    });
  }, []);

  // =========================
  // ✅ 最後の1本（自分用）コピー時の確認モーダル（既存）
  // =========================
  const [confirmState, setConfirmState] = useState({
    open: false,
    reservationId: "",
    url: "",
    resForExpiry: null,
  });

  const openLastLinkConfirm = (reservationId, url, resForExpiry) => {
    setConfirmState({ open: true, reservationId, url, resForExpiry });
  };

  const closeLastLinkConfirm = () => {
    setConfirmState({ open: false, reservationId: "", url: "", resForExpiry: null });
  };

  // =========================
  // ✅ 無料予約キャンセル（追加）
  // =========================
  const [cancelUi, setCancelUi] = useState({
    loadingById: {}, // { [reservationId]: true }
    errorById: {}, // { [reservationId]: "..." }
  });

  const setCancelUiFor = useCallback((reservationId, patch) => {
    setCancelUi((prev) => {
      const loadingById = { ...(prev.loadingById || {}) };
      const errorById = { ...(prev.errorById || {}) };

      if ("loading" in patch) {
        if (patch.loading) loadingById[reservationId] = true;
        else delete loadingById[reservationId];
      }
      if ("error" in patch) {
        if (patch.error) errorById[reservationId] = String(patch.error);
        else delete errorById[reservationId];
      }

      return { ...prev, loadingById, errorById };
    });
  }, []);

  const [freeCancelConfirm, setFreeCancelConfirm] = useState({
    open: false,
    reservationId: "",
    title: "",
    timeLabel: "",
    res: null,
  });

  const openFreeCancelConfirm = (res) => {
    const reservationId = String(res?.id || "").trim();
    if (!reservationId) return;

    setFreeCancelConfirm({
      open: true,
      reservationId,
      title: String(res?.title || res?.serviceName || "予約"),
      timeLabel: formatTimeRange(res?.startAt, res?.endAt),
      res,
    });
  };

  const closeFreeCancelConfirm = () => {
    setFreeCancelConfirm({ open: false, reservationId: "", title: "", timeLabel: "", res: null });
  };

  const cancelFreeReservation = async (res) => {
    const reservationId = String(res?.id || "").trim();
    if (!reservationId) return;

    // UI上の締切ガード（Functions側でも必ず再チェック推奨）
    if (!canCancelFreeReservation(res)) {
      alert(`この予約は開始の${FREE_CANCEL_DEADLINE_MIN}分前を過ぎたためキャンセルできません。`);
      return;
    }

    setCancelUiFor(reservationId, { loading: true, error: "" });

    try {
      // ✅ Functions 名はここでは "cancelReservation" を想定
      // もし既存が別名なら、ここだけ置き換えでOK
      const fn = httpsCallable(functions, "cancelAdminFreeReservation");

      await fn({
        tenantId: TENANT_ID,
        reservationId,
        reason: "user_cancelled_free_reservation",
      });

      // ✅ onSnapshot がある前提：消えるので UI は戻すだけでOK
      setCancelUiFor(reservationId, { loading: false, error: "" });
    } catch (e) {
      console.error("[ReservationsCard] cancelReservation failed:", e);
      setCancelUiFor(reservationId, {
        loading: false,
        error: e?.message || "キャンセルに失敗しました。",
      });
    }
  };

  const confirmFreeCancel = async () => {
    const res = freeCancelConfirm?.res;
    closeFreeCancelConfirm();
    if (!res) return;
    await cancelFreeReservation(res);
  };

  // ✅ リロード後に「ローカル保存した urls/copy」を復元
  useEffect(() => {
    const list = Array.isArray(visibleReservations) ? visibleReservations : [];
    if (!list.length) return;

    setInviteUi((prev) => {
      let changed = false;
      const next = { ...(prev || {}) };

      list.forEach((res, idx) => {
        const reservationId = String(res?.id || "").trim();

        const DEBUG_RESTORE = false; // ←必要な時だけ true
        if (DEBUG_RESTORE) {
          console.log("[ReservationsCard][restore] row", idx, {
            reservationId,
            hasId: !!reservationId,
            title: res?.title,
            serviceName: res?.serviceName,
            auth_uid: res?.auth_uid,
            owner_auth_uid: res?.owner_auth_uid,
            created_by_uid: res?.created_by_uid,
            invited_by_uid: res?.invited_by_uid,
            invite_claimed_users_len: Array.isArray(res?.invite_claimed_users) ? res.invite_claimed_users.length : 0,
            door_labels: res?.door_labels,
            doorLabels: res?.doorLabels,
            door_ids: res?.door_ids,
            keys: Object.keys(res || {}),
          });
        }

        if (!reservationId) return;

        const persisted = loadPersistedInviteState(TENANT_ID, reservationId);
        if (!persisted.urls.length) return;

        const cur = next[reservationId] || { loading: false, error: "", urls: [], copied: {} };

        // 既存より“多い/空”場合だけ復元（不意な上書きを避ける）
        if (!Array.isArray(cur.urls) || cur.urls.length < persisted.urls.length) {
          next[reservationId] = {
            ...cur,
            urls: persisted.urls,
            copied: { ...(cur.copied || {}), ...(persisted.copied || {}) },
          };
          changed = true;
        } else if (persisted.copied && Object.keys(persisted.copied).length) {
          // urlsは上書きしないが、copied は補完だけしてよい
          const mergedCopied = { ...(persisted.copied || {}), ...(cur.copied || {}) };
          next[reservationId] = { ...cur, copied: mergedCopied };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [visibleReservations]);

  const publicOrigin = useMemo(() => {
    const env = String(process.env.REACT_APP_PUBLIC_APP_ORIGIN || "").trim();
    return (env || window.location.origin).replace(/\/+$/, "");
  }, []);

  const copyText = async (text) => {
    await navigator.clipboard.writeText(text);
  };

  const handleCopyOne = async (reservationId, url, isDone, resForExpiry) => {
    try {
      // pending 行はコピー不可
      if (isPendingUrl(url)) return;

      // ✅ もう一度押したら「未コピー」に戻す（コピーはしない）
      if (isDone) {
        setInviteUi((prev) => {
          const cur = prev?.[reservationId] || { loading: false, error: "", urls: [], copied: {} };
          const nextCopied = { ...(cur.copied || {}) };

          nextCopied[url] = false;

          savePersistedInviteState(TENANT_ID, reservationId, resForExpiry || {}, cur.urls || [], nextCopied);

          return {
            ...prev,
            [reservationId]: {
              ...cur,
              copied: nextCopied,
            },
          };
        });
        return;
      }

      // ✅ 初回はコピーして copied=true
      await copyText(url);

      setInviteUi((prev) => {
        const cur = prev?.[reservationId] || { loading: false, error: "", urls: [], copied: {} };
        const nextCopied = { ...(cur.copied || {}), [url]: true };

        savePersistedInviteState(TENANT_ID, reservationId, resForExpiry || {}, cur.urls || [], nextCopied);

        return {
          ...prev,
          [reservationId]: {
            ...cur,
            copied: nextCopied,
          },
        };
      });
    } catch (e) {
      console.warn("[ReservationsCard] copy/toggle failed:", e);
      alert("操作に失敗しました。もう一度お試しください。");
    }
  };

  const confirmLastLinkCopy = async () => {
    const { reservationId, url, resForExpiry } = confirmState;
    closeLastLinkConfirm();
    await handleCopyOne(reservationId, url, false, resForExpiry);
  };

  // =========================
  // ✅ 招待リンク発行（残枠分まとめて発行） + プリロード
  // =========================
  const issueInvites = async (res) => {
  const reservationId = String(res?.id || "").trim();
  if (!reservationId) return;

  const guestCount = pickGuestCount(res);
  const curUi = inviteUi?.[reservationId] || {
    loading: false,
    error: "",
    urls: [],
    copied: {},
  };

  const baseUrls = getRealInviteUrls(curUi.urls);
  const remaining = getRemainingCountForReservation(res, baseUrls);

  // 残枠なし
  if (remaining <= 0) return;

  // 押した瞬間に残枠分の pending 行を出す
  setInviteUi((prev) => {
    const cur = prev?.[reservationId] || {
      loading: false,
      error: "",
      urls: [],
      copied: {},
    };

    const currentRealUrls = getRealInviteUrls(cur.urls);
    const pendingUrls = buildPendingUrls(reservationId, remaining);

    return {
      ...prev,
      [reservationId]: {
        ...cur,
        loading: true,
        error: "",
        urls: [...currentRealUrls, ...pendingUrls],
      },
    };
  });

  try {
    const fn = httpsCallable(functions, "createReservationInviteToken");

    // ✅ 20固定ではなく、残枠分だけ依頼
    const result = await fn({
      tenantId: TENANT_ID,
      reservationId,
      count: remaining,
      baseUrl: publicOrigin,
    });

    const d = result?.data || {};
    const newUrls = Array.isArray(d.urls)
      ? d.urls.map((u) => String(u || "").trim()).filter(Boolean)
      : [];

    if (!newUrls.length) {
      throw new Error(d?.message || "招待リンクを取得できませんでした。");
    }

    setInviteUi((prev) => {
      const cur = prev?.[reservationId] || {
        loading: false,
        error: "",
        urls: [],
        copied: {},
      };

      const currentRealUrls = getRealInviteUrls(cur.urls);

      // guestCount を超えないように丸める
      const merged = Array.from(new Set([...currentRealUrls, ...newUrls])).slice(0, guestCount);

      savePersistedInviteState(
        TENANT_ID,
        reservationId,
        res,
        merged,
        cur.copied || {}
      );

      return {
        ...prev,
        [reservationId]: {
          ...cur,
          loading: false,
          error: "",
          urls: merged,
        },
      };
    });
  } catch (e) {
    console.error("[ReservationsCard] issueInvites failed:", e);

    // pending 行を除去して戻す
    setInviteUi((prev) => {
      const cur = prev?.[reservationId] || {
        loading: false,
        error: "",
        urls: [],
        copied: {},
      };

      const currentRealUrls = getRealInviteUrls(cur.urls);

      return {
        ...prev,
        [reservationId]: {
          ...cur,
          loading: false,
          error: e?.message || "発行に失敗しました。",
          urls: currentRealUrls,
        },
      };
    });
  } finally {
    setUiFor(reservationId, { loading: false });
  }
};

  // =========================
  // UI classes（PossessionsCard 風）
  // =========================
  const iconBoxClass =
    "w-6 h-6 bg-gray-100 text-gray-900 rounded-xl flex items-center justify-center shrink-0 border border-gray-200/50 shadow-sm";

  const primaryCtaClass =
    "w-full py-3.5 rounded-3xl flex items-center justify-center gap-2 font-black text-base active:scale-[0.97] transition-all shadow-lg";

  const closeButtonClass =
    "w-full mt-2 py-3.5 rounded-3xl bg-white border-1 border-gray-400 text-gray-700 " +
    "text-m font-black flex items-center justify-center gap-2 " +
    "hover:bg-gray-100 active:scale-[0.99] transition-all";

  return (
    <>
      <div
        className={`bg-white rounded-3xl p-4 border border-gray-100 shadow-lg shadow-black/5 space-y-1.5 font-sans transition-all duration-500 ${
          isOpen ? "ring-1 ring-gray-200" : ""
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-1 px-0.5 text-gray-900">
          <div className="w-6 h-6 flex items-center justify-center">
            <CalendarClock size={15} />
          </div>
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-[0.1em]">次の予約</p>
        </div>

        {/* CTA Button */}
        <button onClick={onOpenBooking} className={`${primaryCtaClass} bg-gray-900 text-white shadow-gray-200`}>
          <PlusCircle size={20} />
          予約する
        </button>

        {/* ✅ pending_payment の注意（confirmed は一覧に出さない運用） */}
        {pendingAlertOpen && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 px-5 py-3 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] font-black text-amber-900">
                  決済が未完了の予約があります
                </div>

                <div className="mt-1 text-[11px] font-bold text-amber-900/80 leading-relaxed">
                  予約を取り直したい場合は、一度キャンセルボタンを押して、初めからやり直してください。<br />
                </div>

                {pendingCancelError && (
                  <div className="mt-2 text-[11px] font-black text-red-700">
                    {pendingCancelError}
                  </div>
                )}
              </div>
            </div>

            <br />

            <button
              type="button"
              onClick={async () => {
                await onCancelAllPending();
                setPendingAlertOpen(false);
              }}
              disabled={pendingCancelLoading}
              className="block ml-auto shrink-0 rounded-xl border border-amber-300 bg-white px-3 py-2 text-[13px] font-black text-amber-900 shadow-sm disabled:opacity-60"
            >
              {pendingCancelLoading ? "取消中…" : "未完了をキャンセル"}
            </button>
          </div>
        )}
        {/* Toggle Bar */}
        <div
          onClick={onToggle}
          className={`flex items-center justify-between bg-gray-50/30 rounded-xl px-4 py-3 border border-gray-100 cursor-pointer transition-colors active:bg-gray-100 ${
            isOpen ? "opacity-60" : ""
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">

            <div
              className={`text-[13px] font-black tracking-tighter truncate ${
                hasItems ? "text-gray-800" : "text-gray-500"
              }`}
            >
              {summaryText}
            </div>
          </div>
          <ChevronDown
            size={20}
            className={`text-gray-900 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>

        {/* Content */}
        {isOpen && (
          <div className="pt-2 space-y-4 animate-fade-in border-t border-gray-50">
            {hasItems ? (
              <div className="space-y-2.5">
                {visibleReservations.map((res) => {
                  const reservationId = String(res?.id || "").trim();
                  const guestCount = pickGuestCount(res);

                  const inviteTokenCandidates = [
                    res?.invite_token,
                    res?.inviteToken,
                    res?.reservation_invite_token,
                    res?.reservationInviteToken,
                    res?.token,
                    res?.token_id,
                    res?.tokenId,
                  ]
                    .filter((v) => typeof v === "string")
                    .map((v) => v.trim())
                    .filter(Boolean);

                  const isHighlighted =
                    (highlightReservationId && reservationId === String(highlightReservationId).trim()) ||
                    (highlightInviteToken &&
                      inviteTokenCandidates.some(
                        (t) => t === String(highlightInviteToken).trim()
                      ));

                  const ui = inviteUi?.[reservationId] || { 
                    loading: false, 
                    error: "", 
                    urls: [], 
                    copied: {}, 
                    sent: {} 
                  };
                  const urls = Array.isArray(ui.urls) ? ui.urls : [];
                  const loading = ui.loading === true;
                  const error = String(ui.error || "");
                  const copied = ui.copied || {};

                  // ✅ owner/guest 判定
                  const myUid = auth?.currentUser?.uid || "";
                  const isGuest = isInviteGuestReservation(res, myUid);
                  const isFree = isFreeReservation(res);

                  // ✅ 無料キャンセルUI
                  const cancelLoading = cancelUi?.loadingById?.[reservationId] === true;
                  const cancelError = String(cancelUi?.errorById?.[reservationId] || "");
                  const canCancel = isFree && !isGuest && canCancelFreeReservation(res);

                  // ✅ 予約docのカウンタより「表示できているリンク数」を優先（pendingは除外）
                  const realUrls = getRealInviteUrls(urls);
                  const issued = getIssuedCountForReservation(res, realUrls);
                  const remaining = getRemainingCountForReservation(res, realUrls);

                  // ✅ “プリロード中”でも showLinks が true になる
                  const showLinks = urls.length > 0;

                  // ✅ ドア名表示
                  const doorLabelText = (() => {
                    const labels =
                      Array.isArray(res?.door_labels)
                        ? res.door_labels
                        : Array.isArray(res?.doorLabels)
                        ? res.doorLabels
                        : [];

                    const labelsNorm = labels.map((v) => String(v || "").trim()).filter(Boolean);
                    if (labelsNorm.length) return labelsNorm.join(" / ");

                    const ids =
                      Array.isArray(res?.door_ids) ? res.door_ids : Array.isArray(res?.doorIds) ? res.doorIds : [];

                    const idsNorm = ids.map((v) => String(v || "").trim()).filter(Boolean);
                    if (idsNorm.length) return `ドアID ${idsNorm.join(" / ")}`;

                    return "—";
                  })();

                  const isPlanMember = isPlanMemberForThisReservationDoor(res);

                  const claimedTokens = buildClaimedTokenSet(res);
                  const claimedUserMap = buildClaimedUserMap(res);

                  // ✅ pending を除外して claimed/unclaimed を計算
                  const urlsWithToken = (urls || [])
                    .filter((u) => !isPendingUrl(u))
                    .map((u) => {
                      const url = String(u || "").trim();
                      const token = extractInviteTokenFromUrl(url);
                      return token ? { url, token } : null;
                    })
                    .filter(Boolean);

                  const uniq = new Map(); // token -> url
                  for (const x of urlsWithToken) {
                    if (!uniq.has(x.token)) uniq.set(x.token, x.url);
                  }
                  const normalized = Array.from(uniq.entries()).map(([token, url]) => ({ token, url }));

                  const capped = normalized.slice(0, guestCount);

                  const claimed = capped.filter((x) => claimedTokens.has(x.token)).map((x) => x.url);
                  const unclaimed = capped.filter((x) => !claimedTokens.has(x.token)).map((x) => x.url);

                  const reservedUrl = !isPlanMember && unclaimed.length ? unclaimed[unclaimed.length - 1] : "";

                  const inviteUrlsUnclaimed = !isPlanMember
                    ? unclaimed.slice(0, Math.max(0, unclaimed.length - 1))
                    : unclaimed;

                  // ✅ pending 行（プリロード表示用）は末尾にまとめて表示
                  const pendingUrls = urls.filter((u) => isPendingUrl(u));

                  const done = copied[reservedUrl] === true;
                  const sent = ui?.sent?.[reservedUrl] === true;

                  const toDateObj = (ts) =>
  ts?.toDate ? ts.toDate() : new Date(ts);

const formatMonthDay = (ts) => {
  const d = toDateObj(ts);
  if (!d || isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const formatWeekday = (ts) => {
  const d = toDateObj(ts);
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ja-JP", { weekday: "short" });
};

                  return (
                    <div
                      key={reservationId || res.id}
                      className={`bg-white p-5 rounded-3xl border shadow-sm transition-all ${
                        isHighlighted
                          ? "border-gray-200 ring-2 ring-gray-200 bg-gray-50/40 animate-pulse"
                          : "border-gray-100"
                      }`}
                    >
                      <div className="flex items-start gap-4">


                        <div className="flex-1 min-w-0">
                          
                          {/* ===== 上段：日付 + タイトル + 時間 ===== */}
                          <div className="flex gap-4">

                            {/* 日付ブロック */}
                            <div className="w-16 shrink-0">
                              <div
                                    className={`rounded-3xl border py-2 text-center ${
                                      isHighlighted
                                        ? "border-gray-900 bg-gray-100 shadow-md"
                                        : "border-gray-200 bg-gray-50"
                                    }`}
                                  >
                                <div className="text-[18px] font-black text-gray-900 leading-none">
                                  {formatMonthDay(res.startAt)}
                                </div>
                                <div className="text-[14px] font-bold text-gray-900 mt-1">
                                  {formatWeekday(res.startAt)}
                                </div>
                              </div>
                            </div>

                            {/* 右側：タイトル + 時間 */}
                            <div className="flex-1 min-w-0 mt-2.5">

                              <h4 className="text-sm font-black text-gray-900 leading-tight truncate">
                                {res.title || res.serviceName}
                              </h4>

                              <div className="text-sm font-bold text-gray-800 mt-1">
                                {formatTimeRange(res.startAt, res.endAt)}
                              </div>

                            </div>                          
                          </div>

                          {/* ===== 下段：場所・人数 ===== */}
                          <div className="flex text-xs font-bold text-gray-500 pt-2 border-t border-gray-100">
                            <span className="flex items-center gap-1.5">
                              <MapPin size={14} className="text-gray-900" /> {getReservationLocationName(res)}
                            </span>

                            <span className="flex items-center gap-1.5 ml-auto">
                              <User size={14} className="text-gray-900" /> {guestCount}名
                            </span>
                          </div>

                          <div className="mt-4 pt-3 border-t border-gray-50">
                            {/* ✅ 参加者（招待受け取り）向け案内 */}
                            {isGuest && (
                              <div className="mt-3 p-3 rounded-3xl border border-gray-200 bg-gray-50">
                                <div className="text-[12px] font-black text-gray-800 text-center">入場方法</div>
                                <div className="mt-2 text-[11px] text-gray-600 font-bold leading-relaxed">
                                  指定の日時に <span className="font-black">{doorLabelText}</span> のドア開錠リーダーに
                                  QRコードをかざして入場してください。
                                </div>
                              </div>
                            )}

                            {/* ✅ 無料予約（主催者だけ）：キャンセルボタン（追加） */}
                            {!isGuest && isFree && (
                              <div className="mt-3">
                                <button
                                  onClick={() => openFreeCancelConfirm(res)}
                                  disabled={!canCancel || cancelLoading}
                                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black shadow-sm transition-all whitespace-nowrap ${
                                    cancelLoading
                                      ? "bg-gray-200 text-gray-500 cursor-wait"
                                      : !canCancel
                                      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                      : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:text-gray-900 active:scale-95"
                                  }`}
                                  title={
                                    !canCancel
                                      ? `開始の${FREE_CANCEL_DEADLINE_MIN}分前を過ぎるとキャンセルできません`
                                      : "無料体験の予約をキャンセルします"
                                  }
                                >
                                  {cancelLoading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                                  予約をキャンセルする
                                </button>
                                <div className="mt-2 text-[11px] text-gray-600 font-bold leading-relaxed">
                                  体調やお仕事の都合で難しい場合は、キャンセルして取り直せます。
                                  <span className="font-black">（開始の{FREE_CANCEL_DEADLINE_MIN}分前まで）</span>
                                </div>

                                {cancelError && (
                                  <div className="mt-2 p-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-900">
                                    <div className="text-[11px] font-black">キャンセルできませんでした</div>
                                    <div className="text-[11px] mt-1 break-words">{cancelError}</div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ✅ 主催者だけ：招待リンク発行UI（無料は非表示のまま） */}
                            {!isGuest && !isFree && (
                              <div className="mt-3">
                                <button
                                  onClick={() => issueInvites(res)}
                                  disabled={loading || remaining <= 0}
                                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black shadow-sm transition-all whitespace-nowrap ${
                                    loading
                                      ? "bg-gray-200 text-gray-500 cursor-wait"
                                      : remaining <= 0
                                      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                      : "bg-gray-900 text-white hover:bg-gray-900 active:scale-95"
                                  }`}
                                  title={remaining <= 0 ? "残枠がありません" : "残枠分の招待リンクを発行します"}
                                >
                                  {loading ? <Loader2 size={14} className="animate-spin" /> : <LinkIcon size={14} />}
                                  招待リンクを発行する
                                </button>

                                <div className="mt-2 text-[11px] text-gray-600 font-bold leading-relaxed">
                                  招待リンクは <span className="font-black">1人につき1つ</span> を、LINE / SMS / メールで{" "}
                                  <span className="font-black">個別に</span> 送ってください。
                                </div>

                                {error && (
                                  <div className="mt-2 p-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-900">
                                    <div className="text-[11px] font-black">発行できませんでした</div>
                                    <div className="text-[11px] mt-1 break-words">{error}</div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ✅ 主催者だけ：招待リンク一覧（無料は非表示のまま） */}
                            {!isGuest && !isFree && showLinks && (
                              <div className="mt-4 bg-gray-50 border border-gray-100 rounded-3xl p-3">
                                <div className="text-[11px] font-black text-gray-700 text-center">
                                  招待リンク（1リンク=1名）
                                </div>

                                {/* ✅ プラン会員でない場合だけ “自分用（確保）” を分ける */}
                                {!isPlanMember && (
                                  <div className="mt-2 text-[11px] text-gray-600 font-bold leading-relaxed">
                                    <span className="font-black">最後の1つは「自分用」</span>です。<br />
                                    全員に配る場合は、下の「自分用」も招待に使えます。
                                  </div>
                                )}

                                <div className="mt-3 space-y-2">
                                  {/* ✅ 参加済み（受取済みリンクは “参加済み” で残す） */}
                                  {urls
                                    .filter((u) => !isPendingUrl(u))
                                    .map((u, i) => {
                                      const token = extractInviteTokenFromUrl(u);
                                      const isClaimed = token ? claimedTokens.has(token) : false;
                                      if (!isClaimed) return null;

                                      return (
                                        <div
                                          key={`claimed-${u}-${i}`}
                                          className="flex items-center gap-2 p-2 rounded-xl border transition-all bg-white border-gray-200"
                                        >
                                          <div className="text-[10px] font-black text-gray-500 w-5 text-center shrink-0">
                                            {i + 1}
                                          </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2">
                                                <div className="text-[11px] font-black text-gray-800">
                                                  {claimedUserMap.get(token) || "参加者"}
                                                </div>
                                              </div>
                                            </div>
                                          <div className="px-2 py-1 rounded-xl bg-gray-900 text-white text-[11px] font-black whitespace-nowrap">
                                            参加済み
                                          </div>
                                        </div>
                                      );
                                    })}

                                  {/* ✅ 未受取：招待用 */}
                                  {inviteUrlsUnclaimed.map((u, i) => {
                                    const done = copied[u] === true;
                                    const sent = ui?.sent?.[u] === true;

                                    return (
                                      <div
                                        key={`invite-${u}-${i}`}
                                        className={`flex items-center gap-2 p-2 rounded-xl border transition-all ${
                                          done ? "bg-gray-50/60 border-gray-200" : "bg-white border-gray-200"
                                        }`}
                                      >
                                        <div className="text-[10px] font-black text-gray-500 w-5 text-center shrink-0">
                                          {i + 1}
                                        </div>

                                        <input
                                          type="text"
                                          readOnly
                                          value={u}
                                          className="flex-1 bg-transparent text-[11px] text-gray-700 outline-none font-mono min-w-0"
                                        />

                                        <button
                                          onClick={() => {
                                            if (done) {
                                              // コピー状態を戻す
                                              handleCopyOne(reservationId, u, true, res);
                                              return;
                                            }

                                            // ✅ Shareモーダルを開く
                                            setInviteUi((prev) => {
                                              const cur = prev?.[reservationId] || {
                                                loading: false,
                                                error: "",
                                                urls: [],
                                                copied: {},
                                                sent: {},
                                              };

                                              return {
                                                ...prev,
                                                [reservationId]: {
                                                  ...cur,
                                                  sent: {
                                                    ...(cur.sent || {}),
                                                    [u]: true,
                                                  },
                                                },
                                              };
                                            });

                                                setShareModal({
                                                  open: true,
                                                  link: u,
                                                  label: res.title || res.serviceName || "予約招待",
                                                  reservationId,
                                                  dateTime: formatDateTimeLabel(res.startAt, res.endAt),
                                                  location: getReservationLocationName(res),
                                                  address: getReservationAddress(res),
                                                  mapUrl: getReservationMapUrl(res),
                                                });
                                              }}
                                            className={`p-2 rounded-xl border shadow-sm text-xs font-black whitespace-nowrap transition-all ${
                                              done
                                                ? "bg-gray-900 text-white border-gray-900 hover:bg-gray-900"
                                                : sent
                                                ? "bg-gray-50 text-gray-900 border-gray-200 hover:bg-gray-100"
                                                : "bg-white text-gray-700 border-gray-200 hover:text-gray-900"
                                            }`}
                                          title={done ? "コピー状態を戻す" : "送信する"}
                                        >
                                          <span className="flex items-center gap-1">
                                            {done ? <RotateCcw size={14} /> : <MessageCircle size={14} />}
                                            {done
                                              ? "戻す"
                                              : sent
                                              ? "もう一度送る"
                                              : "送る"}
                                          </span>
                                        </button>

                                      </div>
                                    );
                                  })}

                                  {/* ✅ 自分用（確保枠） */}
                                  {!isPlanMember && reservedUrl && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <div className="text-[11px] font-black text-gray-700 text-center">
                                        自分用（確保）または招待用
                                      </div>

                                      {(() => {
                                        const done = copied[reservedUrl] === true;

                                        return (
                                          <div
                                            className={`mt-2 flex items-center gap-2 p-2 rounded-xl border transition-all ${
                                              done
                                                ? "bg-gray-50/60 border-gray-200"
                                                : "bg-white border-gray-200"
                                            }`}
                                          >
                                            <div className="text-[10px] font-black text-gray-500 w-5 text-center shrink-0">
                                              ★
                                            </div>

                                            <input
                                              type="text"
                                              readOnly
                                              value={reservedUrl}
                                              className="flex-1 bg-transparent text-[11px] text-gray-700 outline-none font-mono min-w-0"
                                            />

                                            <button
                                              onClick={() => {
                                                if (done) {
                                                  handleCopyOne(reservationId, reservedUrl, true, res);
                                                  return;
                                                }
                                                openLastLinkConfirm(reservationId, reservedUrl, res);
                                              }}
                                              className={`p-2 rounded-xl border shadow-sm text-xs font-black whitespace-nowrap transition-all ${
                                                done
                                                  ? "bg-gray-900 text-white border-gray-900 hover:bg-gray-900"
                                                  : sent
                                                  ? "bg-gray-50 text-gray-900 border-gray-200 hover:bg-gray-100"
                                                  : "bg-white text-gray-700 border-gray-200 hover:text-gray-900"
                                              }`}
                                              title={done ? "コピー状態を戻す" : "コピー（確認あり）"}
                                            >
                                              <span className="flex items-center gap-1">
                                                {done ? <RotateCcw size={14} /> : <Copy size={14} />}
                                                {done ? "戻す" : "コピー"}
                                              </span>
                                            </button>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {/* ✅ pending（プリロード）行：スケルトン表示（入力/コピー無し） */}
                                  {pendingUrls.map((u, i) => (
                                    <div
                                      key={`pending-${u}-${i}`}
                                      className="flex items-center gap-2 p-2 rounded-xl border bg-white border-gray-200"
                                    >
                                      <div className="text-[10px] font-black text-gray-500 w-5 text-center shrink-0">
                                        …
                                      </div>

                                      <div className="flex-1 min-w-0">
                                        <div className="h-4 rounded bg-gray-200/70 animate-pulse w-[70%]" />
                                        <div className="mt-1 h-3 rounded bg-gray-200/60 animate-pulse w-[40%]" />
                                      </div>

                                      <div className="px-2 py-1 rounded-xl bg-gray-100 text-gray-500 text-[11px] font-black whitespace-nowrap">
                                        発行中…
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div className="text-[10px] text-gray-500 text-center mt-3 leading-relaxed">
                                  ※リンクは <span className="font-mono">/invite/reservation/:token</span> で受け取り処理します
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-3xl border border-gray-100 p-5 text-center">
                <div className="text-[12px] font-black text-gray-800 mb-1">予約をしてみよう！</div>
                <div className="text-[11px] text-gray-500 font-bold leading-relaxed">
                  予約の表示と、招待リンクを送るページです。
                </div>
              </div>
            )}

    <div className="pt-3">
      <button onClick={onToggle} className={closeButtonClass}>
        <ChevronUp size={20} className="text-gray-900" />
        閉じる
      </button>
    </div>
          </div>
        )}

        {/* ✅ 最後の1本コピー確認モーダル（既存） */}
        {confirmState.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl border border-gray-200 p-5">
              <div className="text-sm font-black text-gray-900 text-center">確認</div>

              <div className="mt-3 text-[12px] text-gray-700 font-bold leading-relaxed">
                このリンクは <span className="font-black">「自分用（確保）」</span>として分けてあります。<br />
                招待に使うと <span className="font-black">あなたは入場できなくなります。</span>
                <br />
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={closeLastLinkConfirm}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-black text-sm"
                >
                  キャンセル
                </button>
                <button
                  onClick={confirmLastLinkCopy}
                  className="flex-1 py-2.5 rounded-xl bg-gray-900 text-white font-black text-sm"
                >
                  発行する（コピー）
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ✅ 無料予約キャンセル確認モーダル（追加） */}
      {freeCancelConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl border border-gray-200 p-5">
            <div className="text-sm font-black text-gray-900 text-center">予約をキャンセルしますか？</div>

            <div className="mt-3 text-[12px] text-gray-700 font-bold leading-relaxed">
              <div className="text-gray-900 font-black">{freeCancelConfirm.title}</div>
              <div className="mt-1 text-gray-600">{freeCancelConfirm.timeLabel}</div>
              <div className="mt-3 text-gray-700">
                体調やお仕事の都合で難しい場合は、キャンセルして取り直せます。
                <br />
                <span className="font-black">（開始の{FREE_CANCEL_DEADLINE_MIN}分前まで）</span>
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={closeFreeCancelConfirm}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-black text-sm"
              >
                戻る
              </button>
              <button
                onClick={confirmFreeCancel}
                className="flex-1 py-2.5 rounded-xl bg-amber-600 text-white font-black text-sm hover:bg-amber-700 active:scale-95 transition-all"
              >
                キャンセルする
              </button>
            </div>
          </div>
        </div>
      )}
      <ShareLinkModal
        isOpen={shareModal.open}
        onClose={() =>
          setShareModal({
            open: false,
            link: "",
            label: "",
            reservationId: "",
            dateTime: "",
            location: "",
            address: "",
            mapUrl: "",
          })
        }
        title="招待リンクを送る"
        itemLabel={shareModal.label}
        link={shareModal.link}
        dateTime={shareModal.dateTime}
        location={shareModal.location}
        address={shareModal.address}
        mapUrl={shareModal.mapUrl}
      />
    </>
  );
};

export default ReservationsCard;